import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import type { MemberProposal } from '@/types';
import { useTeamStore } from '@/store/useTeamStore';
import { useChatStore } from '@/store/useChatStore';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { Button } from '@/components/ui/button';
import { MemberForm } from '@/tools/inner-team/components/team/MemberForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Pencil, Loader2 } from 'lucide-react';
import { api, VoucherRequiredError } from '@/lib/api';
import { useAuthStore } from '@/store/useAuthStore';
import { LoginPromptDialog } from '@/components/auth/LoginPromptDialog';
import { VoucherPromptDialog } from '@/components/auth/VoucherPromptDialog';


interface Props {
  proposal: MemberProposal;
  messageId: string;
}

export function MemberProposalCard({ proposal, messageId }: Props) {
  const { addMember, updateMember, members } = useTeamStore();
  const { activeSessionId, addMemberToSession } = useChatStore();
  const user = useAuthStore((s) => s.user);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [voucherPromptOpen, setVoucherPromptOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [generatedAvatarUrl, setGeneratedAvatarUrl] = useState<string | undefined>();
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const savedMemberIdRef = useRef<string | undefined>(undefined);
  const avatarPromiseRef = useRef<Promise<{ avatarImageUrl: string }> | null>(null);

  // Pre-generate avatar
  useEffect(() => {
    if (!proposal.appearanceDescription || generatedAvatarUrl || generatingAvatar || !user) return;
    setGeneratingAvatar(true);
    const promise = api.avatar.generate(undefined, proposal.appearanceDescription!);
    avatarPromiseRef.current = promise;
    promise
      .then(({ avatarImageUrl }) => setGeneratedAvatarUrl(avatarImageUrl))
      .catch((err) => {
        if (err instanceof VoucherRequiredError) {
          setVoucherPromptOpen(true);
        } else {
          console.error(err);
        }
      })
      .finally(() => setGeneratingAvatar(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Backfill: if generation completes after member was already saved via "Anpassen", update the avatar
  useEffect(() => {
    const id = savedMemberIdRef.current;
    if (!generatedAvatarUrl || !id) return;
    const member = members.find((m) => m.id === id);
    if (!member || member.avatarImageUrl) return;
    updateMember(id, { avatarImageUrl: generatedAvatarUrl });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedAvatarUrl, members]);

  // Check if this proposal was already saved (by name match for display)
  const alreadySaved = saved || members.some((m) => m.name === proposal.name);
  const displayMember = savedMemberIdRef.current
    ? members.find((m) => m.id === savedMemberIdRef.current)
    : members.find((m) => m.name === proposal.name);

  function handleSave() {
    if (!user) { setLoginPromptOpen(true); return; }
    const member = addMember(proposal, generatedAvatarUrl);
    savedMemberIdRef.current = member.id;
    // If avatar is still generating, update the member in the store when it completes
    if (!generatedAvatarUrl && avatarPromiseRef.current) {
      avatarPromiseRef.current
        .then(({ avatarImageUrl }) => updateMember(member.id, { avatarImageUrl }))
        .catch(() => {});
    }
    if (activeSessionId) addMemberToSession(activeSessionId, member.id);
    setSaved(true);
    toast.success(`${proposal.name} wurde deinem Team hinzugefügt!`);
  }

  if (alreadySaved) {
    return (
      <div className="mt-2 px-4 pb-1 flex items-center gap-2">
        {displayMember && (
          <CharacterPortrait
            emoji={displayMember.avatarEmoji}
            color={displayMember.avatarColor}
            avatarImageUrl={displayMember.avatarImageUrl}
            size="xs"
          />
        )}
        <div className="text-xs text-muted-foreground italic">
          ✓ {proposal.name} ist in deinem Team
        </div>
      </div>
    );
  }

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-2 mx-4 mb-2 border border-border rounded-xl bg-card p-4 space-y-3"
      data-message-id={messageId}
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <CharacterPortrait
            emoji={proposal.suggestedEmoji}
            color={proposal.suggestedColor}
            avatarImageUrl={generatedAvatarUrl}
            size="md"
          />
          {generatingAvatar && (
            <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
              <Loader2 size={20} className="text-white animate-spin" />
            </div>
          )}
        </div>
        <div>
          <p className="font-semibold text-sm">{proposal.name}</p>
          <p className="text-xs text-muted-foreground">{proposal.role}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="flex-1 gap-1.5" onClick={handleSave}>
          <UserPlus size={14} />
          Hinzufügen
        </Button>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger
            render={<Button size="sm" variant="outline" className="gap-1.5" />}
          >
            <Pencil size={14} />
            Anpassen
          </DialogTrigger>
          <DialogContent className="max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Teammitglied anpassen</DialogTitle>
            </DialogHeader>
            <MemberForm
              initial={{
                name: proposal.name,
                role: proposal.role,
                description: proposal.description,
                values: proposal.values,
                typicalPhrases: proposal.typicalPhrases,
                avatarEmoji: proposal.suggestedEmoji,
                avatarColor: proposal.suggestedColor,
                appearanceDescription: proposal.appearanceDescription,
                preGeneratedAvatarUrl: generatedAvatarUrl,
              }}
              onMemberAdded={(memberId) => {
                savedMemberIdRef.current = memberId;
                // If avatar is still generating, update the member in the store when it completes
                if (!generatedAvatarUrl && avatarPromiseRef.current) {
                  avatarPromiseRef.current
                    .then(({ avatarImageUrl }) => updateMember(memberId, { avatarImageUrl }))
                    .catch(() => {});
                }
                if (activeSessionId) addMemberToSession(activeSessionId, memberId);
              }}
              onSave={() => {
                setSaved(true);
                setEditOpen(false);
                toast.success(`${proposal.name} wurde deinem Team hinzugefügt!`);
              }}
              onCancel={() => setEditOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </motion.div>
    <LoginPromptDialog open={loginPromptOpen} onOpenChange={setLoginPromptOpen} />
    <VoucherPromptDialog open={voucherPromptOpen} onOpenChange={setVoucherPromptOpen} />
    </>
  );
}
